/**
 * Report History
 * Every briefing OmniMind has written, with the evidence it rested on.
 * The feed layout, stats row and unread/archive handling follow the live
 * product. Demo content - see src/demo/analytics.ts and src/demo/live.ts.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/shell/AppShell';
import { Card, Kpi, PageHead, Table, Tag, Bar, DemoBadge } from '../components/ui';
import { ChartFrame, Bars } from '../components/charts';
import { Icon, ICONS } from '../components/shell/icons';
import { REPORT_HISTORY, REPORT_VOLUME } from '../demo/analytics';
import { REPORT_STATS, REPORT_FEED } from '../demo/live';

const TYPES = ['All', 'Daily', 'Weekly', 'Ad-hoc'];

export const ReportHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [type, setType] = useState('All');
  const [query, setQuery] = useState('');
  const [archived, setArchived] = useState(false);
  const [view, setView] = useState<'feed' | 'table'>('feed');

  const rows = REPORT_HISTORY.filter((r) => {
    if (type !== 'All' && r.type !== type) return false;
    if (query && !`${r.title} ${r.id}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <AppShell
      title="Report History"
      sub={`${REPORT_STATS.total} reports`}
      actions={
        <>
          <DemoBadge />
          <div className="srch">
            <Icon d={ICONS.search} strokeWidth={2.2} />
            <input
              placeholder="Search reports…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-p" onClick={() => navigate('/daily-report')}>
            Today's report
          </button>
        </>
      }
    >
      <PageHead
        eyebrow="Report history"
        title="Your personalised AI-generated daily briefings"
        lede="Reports are kept with the sources they read and the confidence they carried, so a number in last month's board pack can still be traced to its record."
        actions={
          <label
            className="chip2"
            style={{ cursor: 'pointer', gap: 7 }}
            title="Include archived reports"
          >
            <input
              type="checkbox"
              checked={archived}
              onChange={(e) => setArchived(e.target.checked)}
              style={{ accentColor: 'var(--blue)' }}
            />
            Show archived
          </label>
        }
      />

      <div className="g g4" style={{ marginBottom: 12 }}>
        <Kpi label="Total reports" value={String(REPORT_STATS.total)} color="var(--blue)" icon="file" />
        <Kpi label="Unread" value={String(REPORT_STATS.unread)} color="var(--purple)" icon="inbox" />
        <Kpi
          label="With critical items"
          value={String(REPORT_STATS.critical)}
          color="var(--red)"
          icon="warn"
          sub="needing a decision"
        />
        <Kpi
          label="Highly rated"
          value={String(REPORT_STATS.highlyRated)}
          color="var(--green)"
          icon="check"
          sub="4 stars or more"
        />
      </div>

      <div className="g g3" style={{ marginBottom: 12 }}>
        <div className="s2">
          <Card
            title="Briefings"
            sub={view === 'feed' ? `${REPORT_FEED.length} most recent` : `${rows.length} shown`}
            flush={view === 'table'}
            right={
              <div className="tabs">
                <button
                  className={`tab${view === 'feed' ? ' on' : ''}`}
                  onClick={() => setView('feed')}
                >
                  Feed
                </button>
                <button
                  className={`tab${view === 'table' ? ' on' : ''}`}
                  onClick={() => setView('table')}
                >
                  Table
                </button>
              </div>
            }
          >
            {view === 'feed' ? (
              REPORT_FEED.map((r) => (
                <button
                  key={r.date}
                  className="feedrow"
                  data-unread={r.unread || undefined}
                  onClick={() => navigate('/daily-report')}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <b style={{ fontSize: 13 }}>{r.day}</b>
                      {r.unread && <Tag tone="b">New</Tag>}
                      <span className="dim" style={{ fontSize: 11 }}>
                        {r.date}
                      </span>
                    </div>
                    <p
                      style={{
                        fontSize: 12.5,
                        color: 'var(--muted)',
                        margin: '4px 0 7px',
                        lineHeight: 1.5,
                      }}
                    >
                      {r.summary}
                    </p>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 7 }}>
                      <span className="cnt" style={{ color: '#0A7A55' }}>
                        ● {r.positive} positive
                      </span>
                      <span className="cnt" style={{ color: '#8A5C05' }}>
                        ▲ {r.concerns} concerns
                      </span>
                      <span className="cnt" style={{ color: 'var(--blue-d)' }}>
                        ✓ {r.actions} actions
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {r.categories.map((c) => (
                        <Tag key={c}>{c}</Tag>
                      ))}
                      <Tag>+{r.more} more</Tag>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flex: 'none' }}>
                    <div
                      className="mono"
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: r.confidence >= 85 ? 'var(--green)' : 'var(--amber)',
                      }}
                    >
                      {r.confidence}%
                    </div>
                    <div className="dim" style={{ fontSize: 10.5 }}>
                      confidence
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <>
                <div className="tabs" style={{ padding: '0 18px 10px' }}>
                  {TYPES.map((t) => (
                    <button
                      key={t}
                      className={`tab${type === t ? ' on' : ''}`}
                      onClick={() => setType(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <Table
                  maxHeight={380}
                  cols={[
                    { t: 'Report' },
                    { t: 'When' },
                    { t: 'Type' },
                    { t: 'Sources', num: true },
                    { t: 'Confidence' },
                    { t: 'Readers', num: true },
                    { t: 'Status' },
                  ]}
                  rows={rows.map((r) => [
                    <div>
                      <div className="t1">{r.title}</div>
                      <div className="t2 mono">{r.id}</div>
                    </div>,
                    <span className="dim">{r.date}</span>,
                    <Tag tone={r.type === 'Daily' ? 'b' : r.type === 'Weekly' ? 'p' : 'n'}>
                      {r.type}
                    </Tag>,
                    r.sources,
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Bar
                        pct={r.confidence}
                        width={54}
                        color={r.confidence >= 80 ? 'var(--green)' : 'var(--amber)'}
                      />
                      <span className="mono">{r.confidence}%</span>
                    </span>,
                    r.readers,
                    <Tag tone={r.tone as any}>{r.status}</Tag>,
                  ])}
                />
              </>
            )}
          </Card>
        </div>

        <ChartFrame title="Reports per week" sub="daily vs ad-hoc" height={300}>
          <Bars
            data={REPORT_VOLUME}
            x="week"
            stacked
            series={[
              { key: 'daily', label: 'Daily' },
              { key: 'adhoc', label: 'Ad-hoc' },
            ]}
          />
        </ChartFrame>
      </div>
    </AppShell>
  );
};
